import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './EventGalleryManager.css';

const EventGalleryManager = () => {
  const [galleries, setGalleries] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [formData, setFormData] = useState({
    event_name: '',
    event_description: '',
    event_date: ''
  });
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';


  // Fetch all galleries
  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/event-galleries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGalleries(response.data);
    } catch (error) {
      console.error('Error fetching galleries:', error);
    }
  };

  const openGalleryDetails = (gallery) => {
    setSelectedGallery(gallery);
    fetchGalleryImages(gallery.id);
  };

  const fetchGalleryImages = async (galleryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/gallery-images/${galleryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveGallery = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (editingGallery) {
        await axios.put(`${API_URL}/admin/event-gallery/${editingGallery.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const response = await axios.post(`${API_URL}/admin/event-gallery`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // After creation, fetch new gallery list
      }
      fetchGalleries();
      closeModal();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    if (!selectedGallery) {
      alert('Select a gallery first');
      return;
    }
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));

    setUploadingImages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/admin/upload-gallery-images/${selectedGallery.id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert(response.data.message);
      fetchGalleryImages(selectedGallery.id);
      fetchGalleries();
    } catch (error) {
      alert(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/gallery-image/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Image deleted');
      fetchGalleryImages(selectedGallery.id);
    } catch (error) {
      alert('Failed to delete image');
    }
  };

  const handleDeleteGallery = async (galleryId) => {
    if (!window.confirm('Delete this gallery?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/event-gallery/${galleryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Gallery deleted');
      fetchGalleries();
      setSelectedGallery(null);
    } catch (error) {
      alert('Failed to delete gallery');
    }
  };

  const openModal = (gallery = null) => {
    setEditingGallery(gallery);
    setFormData({
      event_name: gallery?.event_name || '',
      event_description: gallery?.event_description || '',
      event_date: gallery?.event_date || ''
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingGallery(null);
  };

  return (
    <div className="EventGalleryManager">
      {!selectedGallery ? (
        <div>
          <h2>Galleries</h2>
          <button onClick={() => openModal()}>+ Create Gallery</button>
          <div className="galleries">
            {galleries.map((g) => (
              <div key={g.id} className="gallery-card">
                <h3>{g.event_name}</h3>
                <p>{g.event_description}</p>
                <p>Images: {g.image_count}</p>
                <button onClick={() => openGalleryDetails(g)}>Manage Gallery</button>
                <button onClick={() => openModal(g)}>Edit</button>
                <button onClick={() => handleDeleteGallery(g.id)}>Delete</button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <button onClick={() => setSelectedGallery(null)}>Back to Galleries</button>
          <h2>{selectedGallery.event_name}</h2>
          <p>{selectedGallery.event_description}</p>
          <p>Date: {selectedGallery.event_date}</p>

          {/* Upload multiple images */}
          <div className="upload-section">
            <h3>Upload Images</h3>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploadingImages}
            />
            <span>{uploadingImages ? 'Uploading...' : 'Select images'}</span>
          </div>

          {/* List existing images */}
          <div className="images-list">
            {galleryImages.map((img) => (
              <div key={img.id} className="image-item">
                <img src={img.image_path} alt="" />
                <button onClick={() => handleDeleteImage(img.id)}>Delete</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal for create/edit */}
      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h3>{editingGallery ? 'Edit Gallery' : 'Create Gallery'}</h3>
            <form onSubmit={handleSaveGallery}>
              <input
                type="text"
                name="event_name"
                placeholder="Event Name"
                value={formData.event_name}
                onChange={handleInputChange}
                required
              />
              <textarea
                name="event_description"
                placeholder="Description"
                value={formData.event_description}
                onChange={handleInputChange}
              />
              <input
                type="date"
                name="event_date"
                value={formData.event_date}
                onChange={handleInputChange}
              />
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button onClick={closeModal}>Cancel</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventGalleryManager;
